import { Button, Flag, Radio, RadioGroup, Text } from '@lawpilots/design-system'
import { Box } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useListTenantCourseVariantsQuery } from '@/services/api/tenant/cyclesEnhanced'

type CourseLanguagePickerProps = {
  tenantId: string
  courseCode: string
  /** Id of the visible heading that names the group, so the copy is not repeated for screen readers. */
  labelledBy: string
  selectedVariantId: string | null
  onSelect: (variantId: string) => void
}

// Matches the legacy screen: a long catalogue is revealed a page at a time rather than scrolled,
// so the action row stays in view. Real tenants can carry far more than the five the design shows.
const LANGUAGES_PER_PAGE = 6

const LabelStyles = { display: 'flex', alignItems: 'center', gap: 1 }

// The DS group stacks its items in a single flex column, which leaves a tall ragged list for
// what is usually a handful of languages. Two columns keep it compact. The override is
// unlayered (Emotion) so it wins over the DS recipe's layered rule without !important.
const GroupStyles = {
  '& [role="radiogroup"]': {
    display: 'grid',
    // max-content, not 1fr: the columns hug the longest language name instead of stretching
    // to the panel width, which left a large dead gap between the two lists.
    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, max-content))' },
    columnGap: 4,
    justifyContent: 'start',
  },
}

/**
 * The course-language list (FS-334). One row per variant the tenant has provisioned for this
 * course, each a flag and the served display name, valued by the variant id the start call needs.
 *
 * Sourced from `ListTenantCourseVariants` (`/tenants/{id}/courses/{code}/variants`). The former
 * `ListSubscriptionCourseVariants` was deleted from laas-api by the FS-58 ledger work, which is
 * what left this list empty and made starting a course impossible on the epic branch.
 */
export const CourseLanguagePicker = ({
  tenantId,
  courseCode,
  labelledBy,
  selectedVariantId,
  onSelect,
}: CourseLanguagePickerProps): JSX.Element | null => {
  const { t } = useTranslation('Lmsplus_Course_Intro_Page')
  const { data: variants, isLoading, isError } = useListTenantCourseVariantsQuery({ tenantId, courseCode })

  const [visibleCount, setVisibleCount] = useState(LANGUAGES_PER_PAGE)

  // A selection restored from outside the first page (or a course swapped under us) would
  // otherwise sit hidden behind "show more" with nothing on screen looking chosen.
  const selectedIndex = variants?.findIndex((variant) => variant.id === selectedVariantId) ?? -1
  useEffect(() => {
    if (selectedIndex < 0) return
    setVisibleCount((count) =>
      Math.max(count, Math.ceil((selectedIndex + 1) / LANGUAGES_PER_PAGE) * LANGUAGES_PER_PAGE),
    )
  }, [selectedIndex])

  if (isLoading) return null

  if (isError || !variants?.length) {
    return (
      <Text variant="body-reading" color="secondary">
        {t('course_languages_unavailable')}
      </Text>
    )
  }

  const visible = variants.slice(0, visibleCount)

  return (
    <Box sx={GroupStyles}>
      <RadioGroup value={selectedVariantId} onChange={onSelect} aria-labelledby={labelledBy}>
        {visible.map((variant) => (
          <Radio key={variant.id} value={variant.id}>
            <Box sx={LabelStyles}>
              {/* Decorative: the display name beside it already names the language. */}
              <Flag language={variant.language_tag} decorative />
              {variant.language_display_name}
            </Box>
          </Radio>
        ))}
      </RadioGroup>
      {visibleCount < variants.length && (
        <Button variant="text" onClick={() => setVisibleCount((count) => count + LANGUAGES_PER_PAGE)}>
          {t('show_more_languages')}
        </Button>
      )}
    </Box>
  )
}
